import { useState } from 'react';
import { useIsomorphicLayoutEffect } from 'ahooks';

import { useRentalManagerUserExperimentWebApiHandler_getTreatment } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import accessUtils from '../utils/accessUtils';
import { getCookieAsBoolean } from '../utils/cookieUtils';

import { ZRM_OWNERSHIP_VERIFICATION_ENABLED } from '../constants/FeatureFlagConstants';
import SplitTestConstants from '../constants/SplitTestConstants';

import { useAuth } from '../components/AuthProvider';
import { useGetListingDetails } from './queries/properties';
import { useOwnershipVerificationState } from './queries/useOwnershipVerification';

import type { PropertyOwnershipVerificationState } from '@zg-rentals/ts-rental-manager-api-client';

type OwnershipVerificationAccess = {
  isEnabled: boolean;
  isLoading: boolean;
  state: PropertyOwnershipVerificationState | undefined;
  shouldShowVerificationStep: boolean;
};

const DISABLED: OwnershipVerificationAccess = {
  isEnabled: false,
  isLoading: false,
  state: undefined,
  shouldShowVerificationStep: false,
};

/**
 * Shared by the wizard and Review so both read one gate. Eligibility is resolved before the
 * treatment call, which counts as an exposure.
 */
export const useOwnershipVerificationAccess = ({
  listingAlias,
}: {
  listingAlias: string;
}): OwnershipVerificationAccess => {
  const { isAuthorized } = useAuth();
  const { data: listingDetails } = useGetListingDetails(listingAlias, { enabled: isAuthorized });

  const isEligible =
    isAuthorized && !listingDetails?.isFeed && !listingDetails?.active && Boolean(listingDetails?.forRentBy);

  // Read after mount: during render the cookie is false on the server, mismatching on hydration. Layout
  // effect so a remount (Review) paints with the cached answer instead of a frame of the control.
  const [cookie, setCookie] = useState({ checked: false, enabled: false });

  useIsomorphicLayoutEffect(() => {
    setCookie({ checked: true, enabled: getCookieAsBoolean(ZRM_OWNERSHIP_VERIFICATION_ENABLED) });
  }, []);

  const isRolloutEnabled = accessUtils.isOwnershipVerificationRolloutEnabled();
  const isTreatmentQueryEnabled = isEligible && cookie.checked && !cookie.enabled && isRolloutEnabled;

  const { data, isPending } = useRentalManagerUserExperimentWebApiHandler_getTreatment(
    { featureName: SplitTestConstants.ZRM_OWNERSHIP_VERIFICATION },
    {
      enabled: isTreatmentQueryEnabled,
      staleTime: Infinity,
      throwOnError: false,
      meta: {
        tags: ['ownership-verification-split-treatment'],
        context: {
          api: 'getTreatment',
          details: { featureName: SplitTestConstants.ZRM_OWNERSHIP_VERIFICATION },
        },
      },
    },
  );

  // Alongside the treatment call rather than after it, so enrolled landlords don't wait on both in turn.
  const verification = useOwnershipVerificationState({
    listingAlias,
    enabled: isEligible && (cookie.enabled || isRolloutEnabled),
  });

  if (!isEligible) {
    return DISABLED;
  }

  const isEnabled = cookie.enabled || (isTreatmentQueryEnabled && data?.treatment === 'on');
  const isTreatmentLoading = !cookie.checked || (isTreatmentQueryEnabled && isPending);

  if (!isEnabled) {
    return { ...DISABLED, isLoading: isTreatmentLoading };
  }

  return {
    isEnabled,
    isLoading: isTreatmentLoading || verification.isStateLoading,
    state: verification.state,
    shouldShowVerificationStep: verification.shouldShowVerificationStep,
  };
};
